import { addMinutes, format, getTime } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import React from 'react';
import { useForm } from 'react-hook-form';

import DatePicker from '@components/form/DatePicker/DatePicker';
import TextArea from '@components/form/TextArea/TextArea';
import ModalHeader from '@components/modals/ModalHeader/ModalHeader';
import Button from '@components/uikit/Button/Button';

import { filterPassedTimeForSnooze } from '@helpers/filterPassedTimeForSnooze';
import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useRemindLaterMutation } from '@services/useRemindLaterMutation';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import {
  SNOOZE_ACTION_ITEM_FIELDS,
  schemaSnoozeActionItem,
  snoozeActionItemDefaultValue,
} from './constants/schemaSnoozeActionItem';
import style from './style.module.scss';

interface IProps {
  actionItemId: number;
  facilityTimezone: number;
}

const SnoozeModal: React.FC<IProps> = ({ actionItemId, facilityTimezone }) => {
  const { t } = useTranslation();
  const { setModalClose } = useModalActions();
  const { data: profileData } = useUserProfileQuery();

  const dateTimeFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const utcTime = formatInTimeZone(new Date(), 'UTC', 'yyyy/MM/dd HH:mm');

  const currentFacilityDateTime = addMinutes(utcTime, facilityTimezone * 60);

  const { control, handleSubmit } = useForm({
    mode: 'all',
    resolver: schemaSnoozeActionItem(currentFacilityDateTime),
    defaultValues: snoozeActionItemDefaultValue,
  });

  const { mutate: remindLaterMutate, isLoading: remindLaterIsLoading } = useRemindLaterMutation({
    actionItemId,
  });

  const submit = handleSubmit((formData) => {
    remindLaterMutate(
      {
        action_item_id: actionItemId,
        description: formData.description,
        end_snooze_date: format(addMinutes(formData.date, -facilityTimezone * 60), 'yyyy/MM/dd HH:mm'),
      },
      {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('The action item has been snoozed.') });

          setModalClose();
        },
      },
    );
  });

  return (
    <div className={style.modalSnooze}>
      <ModalHeader />
      <div className={style.modalBody}>
        <div className={style.modalBodyTitle}>{t('Snooze Action Item')}</div>
        <DatePicker
          control={control}
          name={SNOOZE_ACTION_ITEM_FIELDS.DATE}
          dateFormat={dateTimeFormat}
          minDate={currentFacilityDateTime}
          filterTime={(time) => filterPassedTimeForSnooze(time, currentFacilityDateTime)}
          showMonthDropdown
          showYearDropdown
          showTimeSelect
          label={t('Remind Me On This Date')}
          required
          isFacilityOffsetAdded
          timeCaption={format(getTime(currentFacilityDateTime), 'HH:mm')}
        />
        <div className={style.textArea}>
          <TextArea
            control={control}
            name={SNOOZE_ACTION_ITEM_FIELDS.DESCRIPTION}
            label={t('Reason for snooze')}
            required
          />
        </div>
      </div>
      <div className={style.modalFooter}>
        <Button type="primary" text={t('Confirm')} disabled={remindLaterIsLoading} onClick={submit} />
        <Button
          type="secondary"
          text={t('Cancel')}
          disabled={remindLaterIsLoading}
          onClick={() => setModalClose()}
        />
      </div>
    </div>
  );
};

export default SnoozeModal;
